// ABOUTME: Modal for editing subscription plan details and limits.
// ABOUTME: Plan name is read-only; all other fields are editable by application admins.

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { planApi } from '@/services/api/adminClient';
import type { SubscriptionPlanResponse, PlanUpdateRequest, PlanFeaturesAdmin } from '@/types/admin';

const FEATURE_FIELDS: { key: keyof PlanFeaturesAdmin; label: string; description: string }[] = [
  { key: 'allow_password_sharing', label: 'Password Protection', description: 'Allow password-protected sharing' },
  { key: 'allow_email_sharing', label: 'Email Verification', description: 'Allow email-gated sharing' },
  { key: 'allow_comments', label: 'Comments', description: 'Allow comments on shared reports' },
  { key: 'require_branding', label: 'Require Branding', description: 'Force "Powered by Oleum BI" on shared reports' },
  { key: 'allow_self_service_data_config', label: 'Self-Service Data Config', description: 'Allow self-service data source configuration (CSV upload, etc.)' },
];

interface PlanEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: SubscriptionPlanResponse | null;
  onSaved: (plan: SubscriptionPlanResponse) => void;
}

export function PlanEditModal({
  isOpen,
  onClose,
  plan,
  onSaved,
}: PlanEditModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [priceMonthly, setPriceMonthly] = useState(0);
  const [stripePriceId, setStripePriceId] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isPublic, setIsPublic] = useState(false);
  const [isSelfService, setIsSelfService] = useState(false);
  const [maxUsers, setMaxUsers] = useState(0);
  const [maxDataSources, setMaxDataSources] = useState(0);
  const [maxReportsPerMonth, setMaxReportsPerMonth] = useState(0);
  const [maxMessagesPerMonth, setMaxMessagesPerMonth] = useState(0);
  const [maxActiveInvestigations, setMaxActiveInvestigations] = useState(0);
  const [features, setFeatures] = useState<PlanFeaturesAdmin>({
    allow_password_sharing: false,
    allow_email_sharing: false,
    allow_comments: false,
    require_branding: true,
    allow_self_service_data_config: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form with plan values
  useEffect(() => {
    if (plan) {
      setDisplayName(plan.display_name);
      setDescription(plan.description);
      setPriceMonthly(plan.price_monthly_cents);
      setStripePriceId(plan.stripe_price_id || '');
      setSortOrder(plan.sort_order);
      setIsPublic(plan.is_public);
      setIsSelfService(plan.is_self_service);
      setMaxUsers(plan.limits.max_users);
      setMaxDataSources(plan.limits.max_data_sources);
      setMaxReportsPerMonth(plan.limits.max_reports_per_month);
      setMaxMessagesPerMonth(plan.limits.max_messages_per_month);
      setMaxActiveInvestigations(plan.limits.max_active_investigations);
      if (plan.features) {
        setFeatures(plan.features);
      }
    }
  }, [plan, isOpen]);

  const handleSave = async () => {
    if (!plan) return;

    setIsSaving(true);

    try {
      const updateData: PlanUpdateRequest = {
        display_name: displayName,
        description,
        price_monthly_cents: priceMonthly,
        stripe_price_id: stripePriceId || undefined,
        sort_order: sortOrder,
        is_public: isPublic,
        is_self_service: isSelfService,
        limits: {
          max_users: maxUsers,
          max_data_sources: maxDataSources,
          max_reports_per_month: maxReportsPerMonth,
          max_messages_per_month: maxMessagesPerMonth,
          max_active_investigations: maxActiveInvestigations,
        },
        features,
      };

      const updatedPlan = await planApi.update(plan.name, updateData);
      toast.success(`Plan "${updatedPlan.display_name}" updated successfully`);
      onSaved(updatedPlan);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to update plan'
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !plan) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-lg bg-surface-0 border border-surface-200 shadow-modal rounded-sm p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-primary-50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold text-surface-800">
                Edit Plan
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-100 rounded-sm hover:scale-105 transition-colors"
            >
              <X className="w-5 h-5 text-surface-500" />
            </button>
          </div>

          {/* Form */}
          <div className="space-y-4 mb-6">
            {/* Plan name (read-only) */}
            <div>
              <label className="block text-sm font-medium text-surface-600 mb-1.5">
                Plan Name
              </label>
              <div className="w-full px-4 py-3 rounded-sm bg-surface-100 border border-surface-200 text-surface-500 text-sm cursor-not-allowed">
                {plan.name}
              </div>
              <p className="text-xs text-surface-500 mt-1">
                Plan name cannot be changed
              </p>
            </div>

            <Input
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />

            <div>
              <label className="block text-sm font-medium text-surface-600 mb-1.5">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50 resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Price (cents/month)"
                type="number"
                value={priceMonthly}
                onChange={(e) => setPriceMonthly(parseInt(e.target.value, 10) || 0)}
                helperText={`$${(priceMonthly / 100).toFixed(2)}/month`}
              />
              <Input
                label="Sort Order"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              />
            </div>

            <Input
              label="Stripe Price ID"
              placeholder="price_..."
              value={stripePriceId}
              onChange={(e) => setStripePriceId(e.target.value)}
              helperText="Optional: Stripe price ID for paid plans"
            />

            {/* Boolean flags */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-surface-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                />
                Public
              </label>
              <label className="flex items-center gap-2 text-sm text-surface-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSelfService}
                  onChange={(e) => setIsSelfService(e.target.checked)}
                  className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                />
                Self-Service
              </label>
            </div>

            {/* Limits */}
            <div className="pt-2 border-t border-surface-200">
              <h3 className="text-sm font-medium text-surface-800 mb-3">
                Usage Limits
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Max Users"
                  type="number"
                  value={maxUsers}
                  onChange={(e) => setMaxUsers(parseInt(e.target.value, 10) || 0)}
                />
                <Input
                  label="Max Data Sources"
                  type="number"
                  value={maxDataSources}
                  onChange={(e) => setMaxDataSources(parseInt(e.target.value, 10) || 0)}
                />
                <Input
                  label="Max Reports/Month"
                  type="number"
                  value={maxReportsPerMonth}
                  onChange={(e) => setMaxReportsPerMonth(parseInt(e.target.value, 10) || 0)}
                />
                <Input
                  label="Max Messages/Month"
                  type="number"
                  value={maxMessagesPerMonth}
                  onChange={(e) => setMaxMessagesPerMonth(parseInt(e.target.value, 10) || 0)}
                />
                <Input
                  label="Max Active Investigations"
                  type="number"
                  value={maxActiveInvestigations}
                  onChange={(e) => setMaxActiveInvestigations(parseInt(e.target.value, 10) || 0)}
                />
              </div>
            </div>

            {/* Features */}
            <div className="pt-2 border-t border-surface-200">
              <h3 className="text-sm font-medium text-surface-800 mb-3">
                Plan Features
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-200">
                      <th className="text-left py-2 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">Feature</th>
                      <th className="text-center py-2 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FEATURE_FIELDS.map(({ key, label, description: desc }) => (
                      <tr key={key} className="border-b border-surface-100">
                        <td className="py-3 px-3">
                          <p className="font-medium text-surface-800">{label}</p>
                          <p className="text-xs text-surface-500">{desc}</p>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <button
                            type="button"
                            onClick={() => setFeatures((prev) => ({ ...prev, [key]: !prev[key] }))}
                            className="cursor-pointer"
                            title={`Click to ${features[key] ? 'disable' : 'enable'}`}
                          >
                            <span className={`inline-block px-2 py-0.5 text-xs rounded-sm ${
                              features[key]
                                ? 'bg-success-light text-success'
                                : 'bg-surface-100 text-surface-500'
                            }`}>
                              {features[key] ? 'Yes' : 'No'}
                            </span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              isLoading={isSaving}
              className="flex-1"
              leftIcon={!isSaving ? <Save className="w-4 h-4" /> : undefined}
            >
              Save Changes
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default PlanEditModal;
